// @file Vuex Store module for Realtime functionality
import { trackEvent } from '@@/bits/analytics'
import { getSocket } from '@@/bits/brahms'
import { shouldUseBroadcastChannel } from '@@/bits/broadcast_channel'
import environment from '@@/bits/environment'
import { captureException, setScope } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import ping from '@@/bits/ping'
import { NATIVE_HOST } from '@@/bits/url'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceContributingStatusStore } from '@@/pinia/surface_contributing_status'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import type {
  AccumulatedReactions,
  UserId,
  WallCustomPostProperty,
  WallPostProperties,
  WhiteboardCollaborationSettings,
} from '@@/types'
import { useSlideshowRemote } from '@@/vuecomposables/useSlideshowRemote'
import { useSurfaceCustomPropertyResponse } from '@@/vuecomposables/useSurfaceCustomPropertyResponse'
import type { RootState } from '@@/vuexstore/surface/types'
import type { Comment, JsonAPIResponse, Wish } from '@padlet/arvo'
import { debounce } from 'es-toolkit'
import { LongPoll, type Channel, type Socket } from 'phoenix'
import type { Module } from 'vuex'

interface RealtimeState {
  wallChannelLastConnected: number | null
  wallChannelLastDisconnected: number | null
  brahmsToken: string | null
  deviceId: string | null
  brahmsSocket: Socket | null
  wallBrahmsChannel: Channel | null
  deviceBrahmsChannel: Channel | null
  userBrahmsChannel: Channel | null
  dispatchQueue: any[]
  isClearingQueue: boolean
  notificationUid: string | null
  userId: number | null
  isPresencePresent: boolean
  shouldUseLongPoll: boolean
}

const defaultState = (): RealtimeState => ({
  wallChannelLastConnected: null,
  wallChannelLastDisconnected: null,
  brahmsToken: null,
  deviceId: null,
  brahmsSocket: null,
  wallBrahmsChannel: null,
  deviceBrahmsChannel: null,
  userBrahmsChannel: null,
  dispatchQueue: [],
  isClearingQueue: false,
  notificationUid: null,
  userId: null,
  isPresencePresent: false,
  shouldUseLongPoll: false,
})

function userNeedsFocus(): boolean {
  const commentsStore = useCommentsStore()
  const isBusyReacting = !!useReactionsStore().postIdBeingReactedTo
  const isBusyCommenting =
    commentsStore.isCommenting &&
    commentsStore.inlineCommentInputLastTypedInMs > 0 &&
    Date.now() - commentsStore.inlineCommentInputLastTypedInMs < 30000
  return isBusyReacting || isBusyCommenting
}

const RECONNECT_DELAY = [50, 75, 100, 200, 500, 1000, 2000, 5000]
const MAX_ALIVE_BEACON_WAITTIME_MS_FOR_WEBSOCKET = 5000

let serverClientCheckReceivingTimeout: ReturnType<typeof setTimeout> | null = null

const RealtimeModule: Module<RealtimeState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    areUpdatesPending(state): boolean {
      return state.dispatchQueue.length > 0
    },
    isConnected(state): boolean {
      const connectedTime = state.wallChannelLastConnected
      const disconnectedTime = state.wallChannelLastDisconnected
      if (!connectedTime) return false
      if (!disconnectedTime) return true
      return connectedTime > disconnectedTime
    },
    isRealtimeDownSinceLastSync(state, getters): (lastSyncTime: number) => boolean {
      return (lastSyncTime: number): boolean => {
        if (!getters.isConnected) return true
        const connectedTime = state.wallChannelLastConnected as number
        // lastSyncTime is the last time at which wishes were fetched.
        // wallChannelLastConnected is the time at which wall channel was connected via realtime.
        // Because we preload wishes, lastSyncTime is usually slightly less than wallChannelLastConnected
        // when the page first loads.
        // This causes surface to default to unnecessarily re-fetch wishes on the first refresh,
        // i.e. when user returns to the tab after being away for a while.
        // So we subtract 1s from connectedTime, so that we don't default to re-fetching wishes
        // on the first refresh.
        // RISK: if there is any update made to the posts in the duration between wishes loading
        // and wall channel being connected, we will miss it. However, this is a very rare edge case,
        // and the changes are applied only when the user returns to the tab, which is already
        // a very late update.
        return lastSyncTime <= connectedTime - 1000
      }
    },
  },
  mutations: {
    INITIALIZE_STATE(state, data): void {
      Object.assign(state, data)
    },
    SET_BRAHMS_TOKEN(state, brahmsToken): void {
      state.brahmsToken = brahmsToken
    },
    SET_BRAHMS_SOCKET(state, brahmsSocket): void {
      state.brahmsSocket = brahmsSocket
    },
    SET_WALL_BRAHMS_CHANNEL(state, wallBrahmsChannel): void {
      state.wallBrahmsChannel = wallBrahmsChannel
    },
    SET_DEVICE_BRAHMS_CHANNEL(state, deviceBrahmsChannel): void {
      state.deviceBrahmsChannel = deviceBrahmsChannel
    },
    SET_USER_BRAHMS_CHANNEL(state, userBrahmsChannel): void {
      state.userBrahmsChannel = userBrahmsChannel
    },
    QUEUE_UPDATE_ACTION(state, action): void {
      state.dispatchQueue.push(action)
    },
    START_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = true
    },
    CLEAR_DISPATCH_QUEUE(state): void {
      state.dispatchQueue = []
    },
    END_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = false
    },
    SET_NOTIFICATION_UID(state, uid): void {
      state.notificationUid = uid
    },
    CLEAR_NOTIFICATION_UID(state): void {
      state.notificationUid = null
    },
    WALL_CHANNEL_CONNECTED(state): void {
      state.wallChannelLastConnected = Date.now()
    },
    WALL_CHANNEL_DISCONNECTED(state): void {
      state.wallChannelLastDisconnected = Date.now()
    },
    SET_IS_PRESENCE_PRESENT(state, isPresencePresent): void {
      state.isPresencePresent = isPresencePresent
    },
    UPDATE_SHOULD_USE_LONG_POLL(state, shouldUseLongPoll): void {
      state.shouldUseLongPoll = shouldUseLongPoll
    },
    SET_USER_ID(state, userId: UserId): void {
      state.userId = userId
    },
  },
  actions: {
    initializeState({ commit, dispatch }, data): void {
      commit('INITIALIZE_STATE', data)

      // disabled on 2025-06-17. realtimeFetching is globally off so this would always fail.
      // Re-enable this if we bring back realtimeFetching, and are certain that brahms is delivering the server_client_check event.
      // void dispatch('checkIfBrahmsMessagesAreReceived')

      void dispatch('checkIfWebsocketIsEnabled')
    },

    // checkIfBrahmsMessagesAreReceived({ dispatch }): void {
    //   serverClientCheckReceivingTimeout = setTimeout(() => {
    //     if (isAppUsing('brahmsCheck')) {
    //       trackEvent('Realtime', 'Brahms WebSocket server_client_check failed')
    //     }

    //     void dispatch('fallbackToRestApi', null, { root: true })
    //   }, MAX_ALIVE_BEACON_WAITTIME_MS_FOR_WEBSOCKET)
    // },

    checkIfWebsocketIsEnabled({ dispatch }): void {
      if (environment !== 'production') return
      const pingPongTimeout = setTimeout(() => {
        if (isAppUsing('brahmsCheck')) {
          trackEvent('Realtime', 'Diagnostics WebSocket failed')
        }
        void dispatch('fallBackToLongPolling')
      }, MAX_ALIVE_BEACON_WAITTIME_MS_FOR_WEBSOCKET)

      const socket = new WebSocket(`wss://${NATIVE_HOST}/diagnostics/websocket`)
      socket.addEventListener('message', (event) => {
        if (event.data === 'pong') {
          clearTimeout(pingPongTimeout)
          socket.close()
        }
      })
      socket.addEventListener('open', () => {
        socket.send('ping')
      })
    },
    fallBackToLongPolling({ commit, state }): void {
      commit('UPDATE_SHOULD_USE_LONG_POLL', true)
      setScope({ fetchingMode: 'LongPoll' })
      state.brahmsSocket?.replaceTransport(LongPoll)
      state.brahmsSocket?.disconnect()
      state.brahmsSocket?.connect()
    },

    connect({ dispatch, rootGetters }): void {
      if (!rootGetters.isScreenshotMode) {
        setScope({ fetchingMode: 'WebSocket' })
        dispatch('connectToBrahms')
        dispatch('subscribeToStoreChanges')
      }
    },

    connectToBrahms({ dispatch }): void {
      const brahmsConnection: Socket | undefined = window.ww?.brahms?.socket
      if (brahmsConnection === undefined) {
        void dispatch('createNewBrahmsConnection')
        return
      }
      if (window.ww.brahms?.isUsingLongPolling ?? false) {
        if (serverClientCheckReceivingTimeout != null) {
          clearTimeout(serverClientCheckReceivingTimeout)
          serverClientCheckReceivingTimeout = null
        }

        void dispatch('fallbackToRestApi', null, { root: true })
      }
      void dispatch('updateCurrentBrahmsConnection', brahmsConnection)
    },

    disconnectBrahms({ commit, state }): void {
      if (state.brahmsSocket == null) return
      state.brahmsSocket.disconnect()
      commit('SET_BRAHMS_SOCKET', null)
    },

    absent({ commit, state }): void {
      if (state.isPresencePresent) {
        commit('SET_IS_PRESENCE_PRESENT', false)
        if (state.wallBrahmsChannel == null) return
        state.wallBrahmsChannel.push('absent', {})
      }
    },

    present({ commit, state }): void {
      commit('SET_IS_PRESENCE_PRESENT', true)
      if (state.wallBrahmsChannel == null) return
      state.wallBrahmsChannel.push('present', {})
    },

    clearMessageQueue({ dispatch }): void {
      if (window.ww.brahms == null) return
      const messageQueue = window.ww.brahms?.messageQueue
      if (messageQueue == null) return
      void Promise.all(messageQueue.map(async (message) => await dispatch('handleMessage', message)))
      window.ww.brahms.messageQueue = []
    },

    createNewBrahmsConnection({ state, commit, dispatch }): void {
      if (!state.brahmsToken) return
      let connectionRetries = 0
      const brahmsConnection = getSocket(
        { accessToken: state.brahmsToken, deviceId: state.deviceId, shouldUseLongPoll: state.shouldUseLongPoll },
        (tries) => {
          connectionRetries++
          return RECONNECT_DELAY[tries - 1] || 10000
        },
      )
      brahmsConnection?.onError((_, transport, establishedConnections) => {
        if (transport === WebSocket && establishedConnections === 0 && connectionRetries > 1) {
          brahmsConnection?.replaceTransport(LongPoll)
          brahmsConnection?.connect()
        }
      })
      brahmsConnection?.connect()
      commit('SET_BRAHMS_SOCKET', brahmsConnection)

      void dispatch('createWallBrahmsChannel')
      void dispatch('createDeviceBrahmsChannel')
      void dispatch('createUserBrahmsChannel')
    },

    createNewBrahmsConnectionForUser({ commit, dispatch }, payload: { userId: UserId; brahmsToken: string }): void {
      void dispatch('disconnectBrahms')
      commit('SET_USER_ID', payload.userId)
      commit('SET_BRAHMS_TOKEN', payload.brahmsToken)
      void dispatch('createNewBrahmsConnection')
    },

    async updateCurrentBrahmsConnection({ commit, dispatch }, brahmsConnection: Socket): Promise<void> {
      commit('SET_BRAHMS_SOCKET', brahmsConnection)

      const wallBrahmsChannel = window.ww.brahms?.channels?.wall
      await dispatch('addWallBrahmsChannelHandlers', wallBrahmsChannel)
      wallBrahmsChannel?.off('new_msg', window.ww.brahms?.msgRefs?.wall)
      commit('WALL_CHANNEL_CONNECTED')
      commit('SET_WALL_BRAHMS_CHANNEL', wallBrahmsChannel)

      const deviceBrahmsChannel = window.ww.brahms?.channels?.device
      await dispatch('addDeviceBrahmsChannelHandlers', deviceBrahmsChannel)
      deviceBrahmsChannel?.off('new_msg', window.ww.brahms?.msgRefs?.device)
      commit('SET_DEVICE_BRAHMS_CHANNEL', deviceBrahmsChannel)

      const userBrahmsChannel = window.ww.brahms?.channels?.user
      await dispatch('addUserBrahmsChannelHandlers', userBrahmsChannel)
      userBrahmsChannel?.off('new_msg', window.ww.brahms?.msgRefs?.user)
      commit('SET_USER_BRAHMS_CHANNEL', userBrahmsChannel)

      void dispatch('clearMessageQueue')
    },

    createWallBrahmsChannel({ state, dispatch, commit, rootGetters }): void {
      const wallId = rootGetters.wallId
      if (state.brahmsSocket == null) return
      try {
        const wallBrahmsChannel: Channel = state.brahmsSocket.channel(`wall:${wallId}`)
        void dispatch('addWallBrahmsChannelHandlers', wallBrahmsChannel)
        wallBrahmsChannel.join().receive('ok', async () => {
          const isReconnection = state.wallChannelLastConnected != null
          commit('WALL_CHANNEL_CONNECTED')
          if (isReconnection) {
            void dispatch('refresh', { isAutoRefresh: false }, { root: true })
          }
        })
        commit('SET_WALL_BRAHMS_CHANNEL', wallBrahmsChannel)
      } catch (e) {
        captureException(e)
      }
    },

    addWallBrahmsChannelHandlers({ commit, dispatch }, wallBrahmsChannel: Channel): void {
      wallBrahmsChannel?.on('new_msg', (data) => {
        void dispatch('handleMessage', data)
      })
      wallBrahmsChannel?.onError(() => {
        commit('WALL_CHANNEL_DISCONNECTED')
      })
      wallBrahmsChannel?.onClose(() => {
        commit('WALL_CHANNEL_DISCONNECTED')
      })
    },

    createDeviceBrahmsChannel({ state, commit, dispatch }): void {
      if (state.brahmsSocket == null) return
      try {
        const deviceBrahmsChannel: Channel = state.brahmsSocket.channel(`device:${state.deviceId}`)
        void dispatch('addDeviceBrahmsChannelHandlers', deviceBrahmsChannel)
        deviceBrahmsChannel.join()
        commit('SET_DEVICE_BRAHMS_CHANNEL', deviceBrahmsChannel)
      } catch (e) {
        captureException(e)
      }
    },

    addDeviceBrahmsChannelHandlers({ dispatch }, deviceBrahmsChannel: Channel): void {
      deviceBrahmsChannel.on('new_msg', (msg) => {
        void dispatch('handleMessage', msg)
      })
    },

    createUserBrahmsChannel({ state, commit, dispatch }): void {
      if (state.brahmsSocket == null) return
      const userId = state.userId
      if (userId == null) return
      const userChannel: Channel = state.brahmsSocket.channel(`user:${userId}`)
      void dispatch('addUserBrahmsChannelHandlers', userChannel)
      userChannel.join()
      commit('SET_USER_BRAHMS_CHANNEL', userChannel)
    },

    addUserBrahmsChannelHandlers({ dispatch }, userChannel: Channel): void {
      userChannel.on('new_msg', (msg) => {
        void dispatch('handleMessage', msg)
      })
    },

    subscribeToStoreChanges({ dispatch, getters }): void {
      const flushUpdates = (): void => {
        if (getters.areUpdatesPending && !userNeedsFocus()) {
          dispatch('dispatchAllQueuedActions')
        }
      }
      const debouncedFlushUpdates = debounce(flushUpdates, 200, {
        edges: ['leading', 'trailing'],
      })
      this.subscribe((): void => debouncedFlushUpdates())
    },

    pingContributingStatus({ state }): void {
      state.wallBrahmsChannel?.push('ping_contributing_status', {})
    },

    updateContributingStatus({ state }, { status }): void {
      state.wallBrahmsChannel?.push('update_contributing_status', { status })
    },

    async fetchAccumulatedReactions({ state }): Promise<AccumulatedReactions> {
      return await new Promise((resolve, reject) => {
        if (state.wallBrahmsChannel == null) return reject(Error('Undefined wall channel'))
        state.wallBrahmsChannel
          .push('fetch_accumulated_reactions', {}, 5000)
          .receive('ok', (data) => {
            resolve(data)
          })
          .receive('error', (error) => {
            reject(error)
          })
          .receive('timeout', () => reject(Error('Timeout')))
      })
    },

    async fetchComments(
      { state },
      query: { wishId?: number; wishHashid?: string; pageStart?: string },
    ): Promise<JsonAPIResponse<Comment>> {
      return await new Promise((resolve, reject) => {
        if (state.wallBrahmsChannel == null) return reject(Error('Undefined wall channel'))
        state.wallBrahmsChannel
          .push('fetch_comments', query, 5000)
          .receive('ok', (data) => {
            resolve(data)
          })
          .receive('error', (error) => {
            reject(error)
          })
          .receive('timeout', () => reject(Error('Timeout')))
      })
    },

    async fetchWishes({ state }, query: { pageStart?: string }): Promise<JsonAPIResponse<Wish>> {
      return await new Promise((resolve, reject) => {
        if (state.wallBrahmsChannel == null) return reject(Error('Undefined wall channel'))
        state.wallBrahmsChannel
          .push('fetch_wishes', query, 5000)
          .receive('ok', (data) => {
            resolve(data)
          })
          .receive('error', (error) => {
            reject(error)
          })
          .receive('timeout', () => reject(Error('Timeout')))
      })
    },

    handleMessage({ dispatch, rootState, rootGetters, state }, messageObj): void {
      if (!messageObj || !messageObj.event || messageObj.uid === rootState.uid) {
        // Allow messages from same user only for wall_url_changed events
        // since this event needs to originate from and be received by the same user to update the browser URL
        if (messageObj?.event !== 'wall_url_changed') return
      }
      // For dalfe events, we perform a no-op for now since we are not handling it.
      if (messageObj.event.startsWith('proto_')) return

      // For non-device_uploads messages, parse the old protocol (JSON in message field)
      let payload
      try {
        payload = JSON.parse(messageObj.message)
      } catch (e) {
        captureException(e, {
          context: {
            error: 'Message in message object is not valid json',
            messageObj,
          },
        })
        return
      }

      // Signal that realtime is working
      ping('realtime', { rate: 20000 })
      const dispatchNow = async (type: string): Promise<any> => await dispatch(type, payload, { root: true })
      const queue = async (type: string): Promise<any> => await dispatch('queueUpdateAction', { type, payload })
      // We add a delay if the post has an attachment, so that we have sufficient
      // lead time for request coalescing to come into play.
      // See original commit: https://github.com/padlet/mozart/commit/9f9e0d60f3934c0e1c6ef411af97e1e24f429151
      const delay = (type: string): void => {
        const upTo1andHalfSecDelay = (Math.random() + 0.5) * 1000
        if (payload.attachment) {
          setTimeout((): void => {
            queue(type)
          }, upTo1andHalfSecDelay)
        } else {
          queue(type)
        }
      }

      // Like queue(), but for Pinia actions instead of Vuex actions.
      // Actions enqueued here will be deferred when userNeedsFocus() is true.
      const queuePiniaAction = (action: (p: any) => void): void => {
        dispatch('queueUpdateAction', {
          type: 'realtime/executePiniaAction',
          payload: { action, payload },
        })
      }

      // Handle device_uploads messages independently (new protocol - payload at top level)
      if (messageObj.event.startsWith('device_uploads_')) {
        if (isAppUsing('deviceUploads')) {
          void import('@@/pinia/surface_device_uploads').then(({ useSurfaceDeviceUploadsStore }) => {
            useSurfaceDeviceUploadsStore().handleRealtimeMessage(
              messageObj.event,
              payload,
              (replyEvent, replyPayload, replyTimeout) => {
                if (!state.userBrahmsChannel) throw new Error('User brahms channel not connected')
                return state.userBrahmsChannel.push(replyEvent, replyPayload, replyTimeout)
              },
            )
          })
        }
        return
      }

      // Right now we are calling 'remote' actions in the modules themselves.
      // Eventually, the modules will have no concept of remote updates.
      // They'll only have simple CRUD operations that'll be called by this guy.

      const handlers = {
        edit_wall(): void {
          useSurfaceSettingsStore().setScreenReaderMessageBasedOnWallUpdate(payload)

          dispatchNow('updateWall')

          if (useSurfaceTranslationsStore().isViewingTranslatedPadlet) {
            void useSurfaceTranslationsStore().translateWallEntity()
          }

          void useSurfaceSettingsStore()?.updatePreviewAttributes(payload)
          const newDefaultSectionId = payload.wish_arrangement?.default_section_id
          if (newDefaultSectionId != null) {
            useSurfaceSectionsStore().setDefaultSectionAndRecentlyTouchedSection(newDefaultSectionId)
          }
        },
        user_edited_wall(): void {
          const { user } = payload
          void useSurfaceSettingsStore()?.notifyWallSettingsUpdate(rootGetters.user.id, user)
        },
        delete_wall(): void {
          if (rootGetters.isLibraryWall as boolean) {
            navigateTo(rootGetters.libraryDashboardUrl)
          } else {
            navigateTo('/')
          }
        },
        add_section(): void {
          useSurfaceSectionsStore().insertSectionRemote(payload)
          void useSurfaceShareLinksStore().fetchSectionBreakoutLinks()
        },
        edit_section(): void {
          useSurfaceSectionsStore().updateSectionRemote(payload)
        },
        delete_section(): void {
          useSurfaceSectionsStore().removeSectionRemote(payload)
          if (useSurfaceShareLinksStore().sectionBreakoutSection?.id === payload.id) {
            void useSurfaceShareLinksStore().handleBreakoutDisabled()
          }
        },
        add_wish(): void {
          delay('post/newPostRemote')
          useSurfacePostsStore().newPostRemote({ post: payload })
        },
        new_poll_vote(): void {
          delay('post/newPollVoteRemote')
        },
        edit_poll(): void {
          delay('post/editPollRemote')
        },
        edit_wish(): void {
          if ('custom_properties' in payload) {
            const { mergeRealtimeCustomPropertiesPayload } = useSurfaceCustomPropertyResponse()
            payload.custom_properties = mergeRealtimeCustomPropertiesPayload({
              postId: payload.id,
              customProperties: payload.custom_properties,
            })
          }
          delay('post/editPostRemote')
          useSurfacePostsStore().editPostRemote({ post: payload })
        },
        batch_edit_wish(): void {
          delay('post/batchEditPostRemote')
        },
        delete_wish(): void {
          dispatchNow('deletePostRemote')
          useSurfacePostsStore().deletePostRemote({ post: payload })
        },
        wish_hidden_to_wait_for_auto_moderation(): void {
          dispatchNow('post/removePostToWaitForAutoModeration')
        },
        // we don't use delay() for drafts because they only exist for their authors
        // we don't expect that changing an attachment would result in many other connected clients requesting for that new attachment in real time
        add_wish_draft(): void {
          useSurfaceDraftsStore().insertDraftRemote(payload)
        },
        edit_wish_draft(): void {
          useSurfaceDraftsStore().updateDraftRemote(payload)
        },
        delete_wish_draft(): void {
          useSurfaceDraftsStore().removeDraftRemote(payload)
        },
        add_wall_automation(): void {
          void import('@@/pinia/surface_automations_store').then(({ useSurfaceAutomationsStore }) => {
            useSurfaceAutomationsStore().createAutomationRemote(payload)
          })
        },
        edit_wall_automation(): void {
          void import('@@/pinia/surface_automations_store').then(({ useSurfaceAutomationsStore }) => {
            useSurfaceAutomationsStore().updateAutomationRemote(payload)
          })
        },
        delete_wall_automation(): void {
          void import('@@/pinia/surface_automations_store').then(({ useSurfaceAutomationsStore }) => {
            useSurfaceAutomationsStore().deleteAutomationRemote(payload)
          })
        },
        refresh_wall_sections(): void {
          if (isAppUsing('realtimeFetching')) {
            useSurfaceSectionsStore().updateSectionEntities(payload)
          }
        },
        refresh_wishes(): void {
          if (isAppUsing('realtimeFetching')) {
            dispatchNow('post/refreshPosts')
          }
        },
        refresh_comments(): void {
          if (isAppUsing('realtimeFetching')) {
            useCommentsStore().refreshComments(payload)
          }
        },
        refresh_accumulated_reactions(): void {
          if (isAppUsing('realtimeFetching')) {
            void useReactionsStore().refreshAccumulatedReactions(payload)
          }
        },
        refresh_padlet_starting_state(): void {
          if (isAppUsing('realtimeFetching')) {
            dispatchNow('refreshPadletStartingState')
          }
        },
        add_comment(): void {
          useCommentsStore().newCommentRemote({ comment: payload })
        },
        edit_comment(): void {
          useCommentsStore().editCommentRemote({ comment: payload })
        },
        delete_comment(): void {
          useCommentsStore().deleteCommentRemote({ comment: payload })
        },
        update_accumulated_reactions(): void {
          useReactionsStore().updateAccumulatedReactions(payload)
        },
        update_user_reaction(): void {
          useReactionsStore().updateUserReaction(payload)
        },
        // Update aggregated custom property responses for a wish and property.
        update_aggregated_custom_property_responses(): void {
          const { updateAggregatedCustomPropertyResponseRealtime } = useSurfaceCustomPropertyResponse()
          queuePiniaAction(updateAggregatedCustomPropertyResponseRealtime)
        },
        // Current user has set a response to a custom property
        update_user_custom_property_response(): void {
          const { updateUserCustomPropertyResponseRealtime } = useSurfaceCustomPropertyResponse()
          queuePiniaAction(updateUserCustomPropertyResponseRealtime)
        },
        // Current user has removed their response to a custom property.
        delete_user_custom_property_response(): void {
          const { deleteUserCustomPropertyResponseRealtime } = useSurfaceCustomPropertyResponse()
          queuePiniaAction(deleteUserCustomPropertyResponseRealtime)
        },
        add_connection(): void {
          useSurfacePostConnectionStore().newConnectionRemote(payload)
        },
        edit_connection(): void {
          useSurfacePostConnectionStore().newConnectionRemote(payload)
        },
        delete_connection(): void {
          useSurfacePostConnectionStore().deleteConnectionRemote(payload.id)
        },
        contributing_status(): void {
          void useSurfaceContributingStatusStore().updateContributingStatusRemote(payload)
        },
        go_to_slide(): void {
          const { slideNumber } = payload
          void useSlideshowRemote().handleIncomingGoToSlide(slideNumber)
        },
        next_slide(): void {
          void useSlideshowRemote().handleIncomingNextSlide()
        },
        previous_slide(): void {
          void useSlideshowRemote().handleIncomingPreviousSlide()
        },
        ping_contributing_status(): void {
          useSurfaceContributingStatusStore().setContributingStatusIfActive()
        },
        login(): void {
          if (shouldUseBroadcastChannel()) return
          useSurfaceGuestStore().handleNewUserCreated(payload)
        },
        logout(): void {
          if (shouldUseBroadcastChannel()) return
          useCommentsStore().resetComments()
          useSurfaceDraftsStore().resetDrafts()
          useSurfaceGuestStore().handleNewUserCreated(payload)
        },
        collaborator_left(): void {
          void import('@@/pinia/surface_share_panel').then(({ useSurfaceSharePanelStore }) => {
            useSurfaceSharePanelStore().removeCollaboratorRemotely(payload)
          })
        },
        collaborator_create(): void {
          useSurfacePermissionsStore().handleCollaboratorChange(payload)
        },
        collaborator_update(): void {
          useSurfacePermissionsStore().handleCollaboratorChange(payload)
        },
        collaborator_delete(): void {
          useSurfacePermissionsStore().handleCollaboratorChange(payload)
        },
        access_settings_updated(): void {
          useSurfacePermissionsStore().handleAccessSettingsChange()
        },
        wall_follow(): void {
          void import('@@/pinia/surface_wall_follows').then(({ useSurfaceWallFollowsStore }) => {
            useSurfaceWallFollowsStore().updateWallFollows(payload)
          })
        },
        wall_follows_count(): void {
          void import('@@/pinia/surface_wall_follows').then(({ useSurfaceWallFollowsStore }) => {
            useSurfaceWallFollowsStore().updateWallFollowsCount(payload.follows)
          })
        },
        self_harm_post_detected(): void {
          useSurfacePostsStore().showSelfHarmPostAlert(payload)
        },
        self_harm_comment_detected(): void {
          useCommentsStore().showSelfHarmCommentAlert(payload)
        },
        submission_request_updated(): void {
          void useSurfaceShareLinksStore().handleSubmissionRequestUpdated()
        },
        remake_link_updated(): void {
          if (useSurfaceStore().isWhiteboard) {
            void import('@@/pinia/whiteboard_remake_link_store').then(({ useWhiteboardRemakeLinkStore }) => {
              void useWhiteboardRemakeLinkStore().fetchWhiteboardRemakeLink()
            })
          } else {
            void import('@@/pinia/surface_remake_link_store').then(({ useSurfaceRemakeLinkStore }) => {
              void useSurfaceRemakeLinkStore().fetchRemakeLink()
            })
          }
        },
        section_breakout_updated(): void {
          void useSurfaceShareLinksStore().handleSectionBreakoutUpdated()
        },
        section_breakout_disabled(): void {
          void useSurfaceShareLinksStore().handleBreakoutDisabled()
        },
        color_breakout_disabled(): void {
          void useSurfaceShareLinksStore().handleBreakoutDisabled()
        },
        author_breakout_disabled(): void {
          void useSurfaceShareLinksStore().handleBreakoutDisabled()
        },
        single_select_breakout_disabled(): void {
          void useSurfaceShareLinksStore().handleBreakoutDisabled()
        },
        add_wall_custom_post_property(): void {
          const newCustomPostProperty: WallCustomPostProperty = payload.data.attributes
          void useSurfacePostPropertiesStore().insertNewCustomPostProperty(newCustomPostProperty)
        },
        delete_wall_custom_post_property(): void {
          const { property_id: propertyId } = payload
          void useSurfacePostPropertiesStore().removeCustomPostPropertyFromList(propertyId)
        },
        edit_wall_custom_post_property(): void {
          const updateCustomPostProperty: WallCustomPostProperty = payload.data.attributes
          void useSurfacePostPropertiesStore().updateCustomPostPropertyInList(updateCustomPostProperty)
        },
        set_wall_post_properties(): void {
          const wallPostProperties: WallPostProperties = payload.data.attributes
          void useSurfacePostPropertiesStore().setWallPostProperties(wallPostProperties)
        },
        set_comment_attachment_settings(): void {
          void import('@@/pinia/comment_attachment_settings').then(({ useCommentAttachmentSettingsStore }) => {
            void useCommentAttachmentSettingsStore().setCommentAttachmentSettings(payload)
          })
        },
        set_whiteboard_collaboration_settings(): void {
          const settings: WhiteboardCollaborationSettings = payload.data.attributes
          void import('@@/pinia/whiteboard_collaboration').then(({ useWhiteboardCollaborationStore }) => {
            void useWhiteboardCollaborationStore().setCollaborationSettings(settings)
          })
        },
        set_whiteboard_tool_settings(): void {
          const settings = payload
          void import('@@/pinia/whiteboard_tools').then(({ useWhiteboardToolsStore }) => {
            void useWhiteboardToolsStore().setToolSettings(settings)
          })
        },
        wall_frozen(): void {
          useSurfaceStore().handlePadletFrozen()
        },
        wall_unfrozen(): void {
          useSurfaceStore().handlePadletUnfrozen()
        },
        mark_as_template(): void {
          useSurfaceStore().setWallAsTemplate()
        },
        unmark_as_template(): void {
          useSurfaceStore().unsetWallAsTemplate()
        },
        server_client_check(): void {
          // Sanity check
          if (payload.device_id !== state.deviceId || payload.user_id !== state.userId) {
            return
          }

          if (serverClientCheckReceivingTimeout != null) {
            clearTimeout(serverClientCheckReceivingTimeout)
            serverClientCheckReceivingTimeout = null

            if (isAppUsing('brahmsCheck')) {
              if (state.shouldUseLongPoll) {
                trackEvent('Realtime', 'Brahms LongPoll server_client_check succeeded')
              } else {
                trackEvent('Realtime', 'Brahms WebSocket server_client_check succeeded')
              }
            }
          }
        },
        user_name_update(): void {
          if (payload.user == null) return
          const surfaceGuestStore = useSurfaceGuestStore()
          if (payload.user?.id === state.userId) {
            void surfaceGuestStore.updateUserInSurfaceStores(payload.user)
          } else {
            const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
            surfaceUserContributorsStore.updateUser(payload.user)
          }
        },
        user_create(): void {
          // surfaceGuestStore.handleNewUserCreated requires payload to contain both user and arvoConfig
          if (payload?.user == null || payload?.arvoConfig == null) return
          const surfaceGuestStore = useSurfaceGuestStore()
          void surfaceGuestStore.handleNewUserCreated(payload)
        },
        grading_context_file_upload_done(): void {
          if (isAppUsing('grading') && payload === rootGetters.wallId) {
            void import('@@/pinia/surface_grading_panel').then(({ useSurfaceGradingPanelStore }) => {
              useSurfaceGradingPanelStore().isUploadingContextFile = false
            })
          }
        },
        whiteboard_default_frame_background_update(): void {
          void import('@@/pinia/whiteboard_frame_background').then(({ useWhiteboardFrameBackgroundStore }) => {
            void useWhiteboardFrameBackgroundStore().initializeDefaultFrameBackground(payload)
          })
        },
        wall_url_changed(): void {
          const newWallPathname = payload.address as string
          // only update the URL if not empty and the browser is not already at that address
          if (newWallPathname !== '' && window.location.pathname !== `/${newWallPathname}`) {
            // preserve any existing search params (?) and hash params (#)
            const newWallPathnameWithParams = `/${newWallPathname}${window.location.search}${window.location.hash}`
            // update the browser URL in place without adding to browser history so back button works as expected
            window.history.replaceState(window.history.state, '', newWallPathnameWithParams)
          }
        },
        wall_ai_chat_reply_chunk(): void {
          const { chunk, index } = payload
          void import('@@/pinia/surface_ai_chat_store').then(({ useSurfaceAIChatStore }) => {
            const surfaceAIChatStore = useSurfaceAIChatStore()

            let delta = '' as string
            if (typeof chunk === 'string') {
              delta = chunk
            } else if (chunk?.content != null) {
              if (typeof chunk.content === 'string') delta = chunk.content
              else if (typeof chunk.content?.text === 'string') delta = chunk.content.text
            }

            if (delta.length > 0) {
              surfaceAIChatStore.addAssistantMessageDelta(delta, false, index)
            }
          })
        },
        wall_ai_chat_completed(): void {
          const { message_id: messageId } = payload
          void import('@@/pinia/surface_ai_chat_store').then(({ useSurfaceAIChatStore }) => {
            const surfaceAIChatStore = useSurfaceAIChatStore()
            surfaceAIChatStore.addAssistantMessageDelta('\n', true, undefined, messageId)
            surfaceAIChatStore.isLoadingResponse = false
          })
        },
        wall_ai_chat_error(): void {
          void import('@@/pinia/surface_ai_chat_store').then(({ useSurfaceAIChatStore }) => {
            const surfaceAIChatStore = useSurfaceAIChatStore()
            surfaceAIChatStore.isLoadingResponse = false
            useGlobalSnackbarStore().setSnackbar({
              message: __('The Assistant encountered an error. Please try again later.'),
              notificationType: SnackbarNotificationType.error,
            })
          })
        },
        wall_ai_chat_suggestions(): void {
          void import('@@/pinia/surface_ai_chat_store').then(({ useSurfaceAIChatStore }) => {
            const surfaceAIChatStore = useSurfaceAIChatStore()
            const { suggestions } = payload
            surfaceAIChatStore.suggestions = suggestions
          })
        },
        wall_ai_chat_tool_call(): void {
          if (payload.device_id !== state.deviceId) {
            return
          }
          void import('@@/pinia/surface_ai_chat_tools_store').then(({ useSurfaceAIChatToolsStore }) => {
            const surfaceAIChatToolsStore = useSurfaceAIChatToolsStore()
            void surfaceAIChatToolsStore.executeTool(payload)
          })
        },
        exit_ticket_suggestions(): void {
          void import('@@/pinia/surface_guided_template_exit_ticket_store').then(({ useSurfaceExitTicketStore }) => {
            const { suggestions } = payload
            useSurfaceExitTicketStore().setSuggestions(suggestions)
          })
        },
        sample_posts_created(): void {
          void import('@@/pinia/surface_guided_template_exit_ticket_store').then(({ useSurfaceExitTicketStore }) => {
            useSurfaceExitTicketStore().handleSamplePostsCreatedEvent()
          })
        },
      }

      const handler = handlers[messageObj.event]
      handler?.()
    },

    queueUpdateAction({ dispatch, commit, getters }, { type, payload }): void {
      if (userNeedsFocus()) {
        if (!getters.areUpdatesPending) dispatch('setUpdatesPendingNotification')
        commit('QUEUE_UPDATE_ACTION', { type, payload })
      } else {
        dispatch(type, payload, { root: true })
      }
    },

    setUpdatesPendingNotification({ dispatch, commit }): void {
      const uid = useGlobalSnackbarStore().setSnackbar({
        message: __('Padlet updated elsewhere.'),
        actionText: __('Update'),
        actionTextActions: [async () => await dispatch('dispatchAllQueuedActions')],
        persist: true,
      })

      commit('SET_NOTIFICATION_UID', uid)
    },

    clearUpdatesPendingNotification({ commit, state }): void {
      if (state.notificationUid != null) {
        useGlobalSnackbarStore().removeSnackbar(state.notificationUid)
      }
      commit('CLEAR_NOTIFICATION_UID')
    },

    dispatchAllQueuedActions({ commit, dispatch, state }): void {
      if (state.isClearingQueue) return
      commit('START_CLEARING_DISPATCH_QUEUE')
      state.dispatchQueue.forEach(
        async ({ type, payload }): Promise<any> => await dispatch(type, payload, { root: true }),
      )
      dispatch('clearDispatchQueue')
      commit('END_CLEARING_DISPATCH_QUEUE')
    },

    clearDispatchQueue({ dispatch, commit }): void {
      dispatch('clearUpdatesPendingNotification')
      commit('CLEAR_DISPATCH_QUEUE')
    },

    /**
     * This method allows us to queue the execution of a Pinia action without
     * manipulating the rest of the realtime logic.
     * TODO: remove this once the realtime Vuex store is migrated to Pinia.
     */
    executePiniaAction(_, { action, payload }): void {
      action(payload)
    },
  },
}

export default RealtimeModule
export type { RealtimeState }
